/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import startCase from 'lodash/startCase';
import * as Yup from 'yup';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import { makeStyles } from '@material-ui/core/styles';

import { FormActions, FormFields, RiskData, Translate } from 'components';
import { RiskContext } from 'forms';
import { DeductiblesContext } from 'modules';

import config from 'config';
import { enqueueNotification, showModal } from 'stores';
import * as utils from 'utils';

import RenderStep from './RenderStep';

import styles from './AddEditQuoteBind.style';

function getSteps(steps) {
  return steps.map((step) => capitalize(step));
}

const MultiStepForm = ({
  fields,
  isReQuote,
  isEndorsement,
  defaultValues,
  riskDataValues,
  handleSubmit,
  productType,
  handleDraftSave,
  fullScreen,
}) => {
  const dispatch = useDispatch();
  const classes = makeStyles(styles, { name: 'MultiStepForm' })({ fullScreen });
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [stepFields, setStepFields] = useState([]);
  const [progress, setProgress] = useState(0);
  const [validationSchema, setValidationSchema] = useState([]);
  const history = useHistory();
  const { riskFieldOptions } = useContext(RiskContext);

  const {
    confirm,
    setConfirm,
    setDeductibleOptions,
    setSelectedDeductible,
    selectedDeductible,
    deductibleOptions,
    setError: setDeductibleError,
    error: deductibleError,
  } = useContext(DeductiblesContext);
  useEffect(() => {
    setProgress(activeStep ? (activeStep / stepFields.length) * 100 : 0);
  }, [activeStep]);

  const methods = useForm({
    shouldUnregister: false,
    defaultValues,
    resolver: yupResolver(utils.form.getValidationSchema(validationSchema)),
    mode: 'onChange',
  });

  const { formState, getValues } = methods;

  const groups = utils.risk.getGroups(fields);
  const groupsTitles = Object.entries(groups).map((group) => startCase(group[0]));

  const groupsIDs = Object.entries(groups).map((group) => group[0]);

  const steps = [...getSteps(groupsTitles), utils.string.t('risks.addRiskConfirmation')];

  const validateFields = useMemo(
    () =>
      fields.filter((field) => {
        const condition = utils.risk.getCondition(field, fields, definitionsFields);
        const isValid = !condition || (condition && utils.risk.isConditionValid(condition, methods.watch()));

        if (field.name === 'expiryDate') {
          const inceptionDate = getValues('inceptionDate');
          const minExpiryDate = utils.date.addDays(utils.date.startOf(inceptionDate, 'day'), 1);
          if (utils.date.isValid(inceptionDate)) {
            field.validation = Yup.date().nullable().min(inceptionDate, utils.string.t('risks.expiryDateInvalidMin'));
            field.muiPickerProps = {
              ...field.minExpiryDate,
              minDate: minExpiryDate,
            };
            field.disabled = Boolean(riskFieldOptions?.policyDuration);
          }
        }

        return !!isValid;
      }),
    [fields, formState]
  );

  const copyFieldNames = useMemo(() => {
    const copyFields = fields
      .map((field) => {
        if (field.copyValue) return field.copyValue;
        return null;
      })
      .filter((field) => field !== null);

    return Array.from(new Set(copyFields));
  }, [fields]);

  // Get fields from arrayItemDef to watch
  const copyFieldArrayNames = useMemo(() => {
    const copyFields = fields
      .flatMap((field) => {
        // Temporary format for arrayItemDef to watch. Example `item:field[X].watch`
        if (field.arrayItemDef) return field.arrayItemDef.map((item) => (item.copyValue ? `${item.name}:${item.copyValue}` : null));
        return null;
      })
      .filter(Boolean);

    return Array.from(new Set(copyFields)).map((a) => {
      const [item, array] = a.split(':');
      const [field, watch] = array.split('[X].');
      // Field - name of array e.g. buildings
      // Watch - name of field from arrayItemDef to watch
      // Item - name of field from arrayItemDef to paste
      return { field, watch, item };
    });
  }, [fields]);

  // Get arrays to watch
  const copyFieldArrays = methods.watch(copyFieldArrayNames.map((item) => item.field));

  // Get fields to watch
  const copyFieldArrayValuesNames = copyFieldArrays.flatMap(
    (array, arrayIndex) =>
      array?.map((_, itemIndex) => `${copyFieldArrayNames[arrayIndex].field}[${itemIndex}].${copyFieldArrayNames[arrayIndex].watch}`) ?? []
  );

  // Get fields to paste
  const copyFieldArrayItemNames = copyFieldArrays.flatMap(
    (array, arrayIndex) =>
      array?.map((_, itemIndex) => `${copyFieldArrayNames[arrayIndex].field}[${itemIndex}].${copyFieldArrayNames[arrayIndex].item}`) ?? []
  );

  // Get values to copy
  const copyFieldArrayValues = methods.watch(copyFieldArrayValuesNames);
  // Cached values are values from copyValue action. Cache is used to avoid overriding user input
  const [cachedCopyFieldArrayValues, setCachedCopyFieldArrayValues] = useState([]);

  useEffect(() => {
    if (isEqual(cachedCopyFieldArrayValues, copyFieldArrayValues)) return;

    copyFieldArrayItemNames.forEach((field, index) => {
      if (copyFieldArrayValues[index] !== cachedCopyFieldArrayValues[index]) {
        methods.setValue(field, copyFieldArrayValues[index]);
      }
    });

    setCachedCopyFieldArrayValues([...copyFieldArrayValues]);
  }, [copyFieldArrayValues]);

  const copyFieldsValues = methods.watch(copyFieldNames);
  const [cachedCopyFieldsValues, setCachedCopyFieldsValues] = useState([]);

  useEffect(() => {
    if (isEqual(cachedCopyFieldsValues, copyFieldsValues)) return;
    setCachedCopyFieldsValues(copyFieldsValues);

    fields.forEach((field) => {
      if (field.copyValue) {
        const copyValueIndex = copyFieldNames.indexOf(field.copyValue);
        // Do not copy if value is not changed
        if (formState.dirtyFields && Object.keys(formState.dirtyFields)?.includes(field.copyValue))
        methods.setValue(field.name, copyFieldsValues[copyValueIndex]);
      }
    });
  }, [copyFieldsValues]);

  useEffect(() => {
    const fieldGroups = { ...groups };
    const fieldGroupsArray = Object.entries(fieldGroups).map((group, index) => group);
    setStepFields(fieldGroupsArray);
  }, [fields]);

  useEffect(() => {
    const updateSchema = !isEqual(
      validationSchema.filter((field) => field.name),
      validateFields.filter((field) => field.name)
    );
    if (updateSchema) setValidationSchema(validateFields);
  }, [formState]);

  const totalSteps = () => steps.length;

  const completedSteps = () => Object.keys(completed).length;

  const isLastStep = () => activeStep === totalSteps() - 1;

  const allStepsCompleted = () => completedSteps() === totalSteps() - 1;

  const isDeductibleStep = (step) => stepFields?.[step]?.[1].find((field) => utils.risk.isDeductible(field)) ?? false;

  const isDeductibleValid = () => {
    const deductibleField = fields.find((field) => utils.risk.isDeductible(field));
    if (!deductibleField) return true;

    const deductibleOptionsLength = Object.keys(deductibleOptions).length;
    const selectedDeductibleLength = Object.keys(selectedDeductible).length;

    return selectedDeductibleLength > 0 && selectedDeductibleLength === deductibleOptionsLength;
  };

  const resetDeductiblePage = () => {
    const deductibleStep = stepFields.findIndex((step) => step[1].find((field) => utils.risk.isDeductible(field)));
    setConfirm(false);
    setDeductibleOptions({});
    setSelectedDeductible({});
    setDeductibleError(false);
    setCompleted({ ...completed, [deductibleStep]: false });
  };

  const handlePromptDeductibleCancel = (callback) => {
    dispatch(
      showModal({
        type: 'CONFIRM',
        component: 'CONFIRM',
        props: {
          title: utils.string.t('risks.deductible.titleLeaveWarning'),
          subtitle: utils.string.t('risks.deductible.titleLeaveWarningHint'),
          fullWidth: true,
          maxWidth: 'sm',
          disableEscapeKey: true,
          componentProps: {
            cancelLabel: utils.string.t('app.cancel'),
            confirmLabel: utils.string.t('app.confirm'),
            submitHandler: () => {
              resetDeductiblePage();
              callback?.();
            },
          },
        },
      })
    );
  };

  const handleStep = (step) => () => {
    if (confirm && !isDeductibleStep(step)) {
      if (deductibleError) {
        resetDeductiblePage();
        setActiveStep(step);
      } else {
        handlePromptDeductibleCancel(() => setActiveStep(step));
      }
    } else {
      setActiveStep(step);
    }
  };

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
  };

  const handleNext = async () => {
    const schemaFields = stepFields[activeStep] ? stepFields[activeStep][1] : [];

    const validateFields = schemaFields
      .filter((field) => {
        const condition = utils.risk.getCondition(field, fields, definitionsFields);
        const isValid = !condition || (condition && utils.risk.isConditionValid(condition, methods.watch()));

        return !!isValid;
      })
      .map((field) => field.name)
      .filter((field) => !!field);

    const isStepValid = await methods.trigger(validateFields);

    if (isDeductibleStep(activeStep)) {
      if (!isDeductibleValid()) {
        dispatch(enqueueNotification(utils.string.t('risks.deductible.missingDeductible'), 'error'));
        return;
      }
    }

    if (isStepValid) {
      handleComplete();
      // It's the last step, but not all steps have been completed,
      // find the first step that has been completed
      const nextStep = steps.findIndex((step, i) => !(i in completed)) > -1 ? steps.findIndex((step, i) => !(i in completed)) : 0;

      const newActiveStep = isLastStep() && !allStepsCompleted() ? nextStep : activeStep + 1;
      setActiveStep(newActiveStep);
    }
  };

  const handleBack = () => {
    if (confirm && isDeductibleStep(activeStep)) {
      if (deductibleError) {
        resetDeductiblePage();
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
      } else {
        handlePromptDeductibleCancel(() => setActiveStep((prevActiveStep) => prevActiveStep - 1));
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const onSave = async () => {
    const data = methods.getValues();
    if (data.clientId && utils.generic.isValidObject(data.clientId)) {
      handleDraftSave(data);
    } else {
      methods.trigger('clientId', { shouldFocus: true });
    }
  };

  const onSubmit = async (data) => {
    const result = { ...data };
    if (!isDeductibleValid()) {
      dispatch(enqueueNotification(utils.string.t('risks.deductible.missingDeductible'), 'error'));
      return;
    }
    const deductibleField = fields.find((field) => utils.risk.isDeductible(field));
    if (deductibleField) {
      result[deductibleField.name] = Object.keys(deductibleOptions).reduce((acc, key) => {
        acc[key] = deductibleOptions[key].map((option) => ({
          ...option,
          selected: selectedDeductible[key].deductible === option.deductible,
        }));
        return acc;
      }, {});
      result.externalSourceId = data[`${deductibleField.name}_externalSourceId`];
      delete result[`${deductibleField.name}_externalSourceId`];
    }

    const res = await handleSubmit(result);
    if (res?.result?.data) {
      const { id } = res.result.data;

      !isReQuote && history.push(`${config.routes.quoteBind.riskDetails}/${id}`);
    }
  };

  const onError = (errors, e) => console.log(errors, e);

  function getStepContent(fieldsArray) {
    return fieldsArray[1]?.length ? (
      <RenderStep fieldsArray={fieldsArray[1]} definitionsFields={definitionsFields} classes={classes} options={{ gridSize: { xs: 12, md: 4, xl: 4 } }} />
    ) : null;
  }

  const definitionsFields = fields?.filter((definition) => definition.type?.toUpperCase() !== 'LABEL');
  return (
    <div className={classes.root}>
      <Stepper alternativeLabel activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} onClick={handleStep(index)} completed={completed[index]}>
            <StepLabel
              StepIconProps={{
                classes: { root: classes.iconContainer },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <LinearProgress variant="determinate" value={progress} className={classes.linearProgress} />
      <FormProvider {...methods}>
        <Box>
          <form>
            <div type="dialog" autoComplete="off" data-testid="risk-form">
              <FormFields type="dialog" nestedClasses={{ root: isLastStep() ? classes.summary : classes.formContent }} step={activeStep}>
                {stepFields[activeStep] ? (
                  getStepContent(stepFields[activeStep])
                ) : isLastStep() ? (
                  <RiskData
                    allStepsCompleted={allStepsCompleted()}
                    productType={productType}
                    handleStep={handleStep}
                    groups={groupsIDs}
                    definitionsFields={definitionsFields}
                    riskDataValues={riskDataValues}
                    isReQuote={isReQuote}
                    isEndorsement={isEndorsement}
                  />
                ) : null}
              </FormFields>
              <FormActions type="dialog">
                {!isReQuote ? (
                  <Button onClick={onSave} color="secondary" variant="contained">
                    {utils.string.t('app.saveDraft')}
                  </Button>
                ) : null}
                <Button disabled={activeStep === 0} onClick={handleBack} className={classes.button}>
                  {utils.string.t('app.backToPrevious')}
                </Button>
                <Button
                  variant="contained"
                  disabled={formState.isSubmitting}
                  color="primary"
                  onClick={() => (isLastStep() && formState.isValid ? methods.handleSubmit(onSubmit, onError)() : handleNext())}
                >
                  {isLastStep() && formState.isValid ? <Translate label="app.submit" /> : `Next`}
                </Button>
              </FormActions>
            </div>
          </form>
        </Box>
      </FormProvider>
    </div>
  );
};

export default MultiStepForm;
